/**
 * Must stay accurate to what the app actually does — see netlify/functions/negotiate.ts,
 * netlify/functions/checkout.ts, and src/atlasAuth.ts. This is disclosure, not marketing
 * copy; if the code changes what's collected or why, this file has to change with it.
 */
export function PrivacyPolicy({ onNavigate }: { onNavigate: (path: string) => void }) {
  return (
    <article className="legal">
      <a
        href="/"
        className="back"
        onClick={(e) => {
          e.preventDefault()
          onNavigate('/')
        }}
      >
        ← Back to Scratchyo
      </a>
      <h1>Privacy Policy</h1>
      <p className="updated">Last updated 2026-07-25</p>

      <p>
        Scratchyo is a fictional indie storefront from a television show — a parody, not a real
        marketplace. This policy covers the real account system and real data behind the joke.
      </p>

      <h2>What we collect</h2>
      <ul>
        <li>
          <strong>Account info:</strong> the email and password you register with, handled by our
          shared account system (GameCodex Accounts / "Atlas"). We don't see or store your raw
          password — Atlas does, in hashed form.
        </li>
        <li>
          <strong>Nothing about your checkout attempts.</strong> The listing title, the price you
          choose, and your account email are sent to Atlas and, through it, to Anthropic's Claude
          API to generate the "Suggested Price" and "Cart" responses — and then discarded. We don't
          persist a cart, an order, a price history, or a log of what either AI persona said to you.
        </li>
        <li>
          We do not use third-party advertising or analytics trackers. Your session token is kept
          in your browser's local storage so you stay signed in — that's the only thing local
          storage is used for.
        </li>
      </ul>

      <h2>Why we collect it</h2>
      <p>
        To run the account system and let you check out (or, more precisely, fail to check out —
        see the AI Policy in our{' '}
        <button className="link" onClick={() => onNavigate('/terms')}>
          Terms
        </button>
        ). We don't have a use for your data beyond the moment it takes to generate a response,
        which is why we don't keep it.
      </p>

      <h2>Who we share it with</h2>
      <p>
        Two processors, both acting on our behalf and under contract — never sold, never handed to
        advertisers:
      </p>
      <ul>
        <li>
          <strong>Atlas</strong> (our own account/identity backend) — stores your account.
        </li>
        <li>
          <strong>Anthropic</strong> — we send your checkout attempt's title, price, and email to
          Anthropic's Claude API, through Atlas, to generate the automatic negotiation and
          checkout-failure responses. See the AI Policy for detail. Anthropic does not receive your
          password.
        </li>
      </ul>

      <h2>How long we keep it</h2>
      <p>
        Account data is kept for as long as your account exists. Checkout attempts (listing, price,
        email, and the AI-generated response) are not stored at all — they exist only for the
        duration of the request that generates them. Email{' '}
        <a href="mailto:legal@wogd.com">legal@wogd.com</a> to request access to or deletion of your
        account data, or to close your account.
      </p>

      <h2>Children</h2>
      <p>
        Scratchyo is not directed at, and accounts are not open to, anyone under 18. We do not
        knowingly collect information from anyone under 18.
      </p>

      <h2>Changes</h2>
      <p>We may update this policy as the product changes. The current version always lives here.</p>

      <h2>Contact</h2>
      <p>
        Questions, access requests, or deletion requests: <a href="mailto:legal@wogd.com">legal@wogd.com</a>.
      </p>
    </article>
  )
}
